'use client';

/**
 * Inline Nano Banana background generator for the deck editor.
 *
 * Mirrors the reference-image workflow from /studio:
 *   - Pick multiple reference images from the photoslop library, brand
 *     subjects (robin, marv, ben, …), or fresh uploads.
 *   - Cite each ref in the prompt with `@1`, `@2`, … chips.
 *   - POST the assembled `{ images, prompt, aspectRatio: '16:9' }` payload to
 *     `/api/photoslop/generate` (Studio path) and poll the resulting job.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface GeneratePanelProps {
  onPick: (path: string) => void;
  onError?: (msg: string) => void;
}

interface SlotEntry {
  path: string;
  label: string;
}

interface LibraryItem {
  filename: string;
  path: string;
  mtime: number;
}

interface LibraryResponse {
  photoslop: LibraryItem[];
  uploads: LibraryItem[];
  generated: LibraryItem[];
  exports: LibraryItem[];
  counts: Record<'photoslop' | 'uploads' | 'generated' | 'exports', number>;
}

interface BrandAsset {
  filename: string;
  path: string;
  mtime: number;
  variant: string;
}

interface BrandFolder {
  key: string;
  label: string;
  count: number;
  cover?: string;
  variants: string[];
}

interface BrandsResponse {
  folders: BrandFolder[];
  assetsByFolder: Record<string, BrandAsset[]>;
}

const MAX_SLOTS = 6;

type PickerSource = 'library' | 'brand' | 'upload';
type LibraryTab = 'photoslop' | 'uploads' | 'generated' | 'exports';

export function GeneratePanel({ onPick, onError }: GeneratePanelProps) {
  const [slots, setSlots] = useState<(SlotEntry | null)[]>(() =>
    Array.from({ length: MAX_SLOTS }, () => null),
  );
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');

  const promptRef = useRef<HTMLTextAreaElement>(null);

  const filledCount = slots.filter(Boolean).length;

  // ── Slot helpers ──────────────────────────────────────────────────────
  const addToFirstEmpty = useCallback((entry: SlotEntry) => {
    setSlots((prev) => {
      const idx = prev.findIndex((s) => s === null);
      if (idx < 0) return prev; // all full — silently no-op
      const next = [...prev];
      next[idx] = entry;
      return next;
    });
  }, []);

  const setSlot = useCallback((index: number, entry: SlotEntry | null) => {
    setSlots((prev) => prev.map((s, i) => (i === index ? entry : s)));
  }, []);

  const clearSlot = useCallback((index: number) => {
    setSlots((prev) => prev.map((s, i) => (i === index ? null : s)));
  }, []);

  // ── Prompt token insertion (matches /studio behaviour) ────────────────
  const insertToken = useCallback((slotIndex: number) => {
    const token = `@${slotIndex + 1}`;
    const ta = promptRef.current;
    if (!ta) {
      setPrompt((p) => `${p}${p && !p.endsWith(' ') ? ' ' : ''}${token} `);
      return;
    }
    const start = ta.selectionStart ?? ta.value.length;
    const end = ta.selectionEnd ?? ta.value.length;
    const before = ta.value.slice(0, start);
    const after = ta.value.slice(end);
    const needsLeading = before.length > 0 && !/\s$/.test(before);
    const insert = `${needsLeading ? ' ' : ''}${token} `;
    const next = `${before}${insert}${after}`;
    setPrompt(next);
    requestAnimationFrame(() => {
      ta.focus();
      const cursor = (before + insert).length;
      ta.setSelectionRange(cursor, cursor);
    });
  }, []);

  // ── @N validation ─────────────────────────────────────────────────────
  const danglingRefs = useMemo(() => {
    const out = new Set<number>();
    for (const m of Array.from(prompt.matchAll(/@(\d+)\b/g))) {
      const n = parseInt(m[1] ?? '', 10);
      if (!Number.isFinite(n) || n < 1 || n > MAX_SLOTS) continue;
      if (!slots[n - 1]) out.add(n);
    }
    return Array.from(out).sort((a, b) => a - b);
  }, [prompt, slots]);

  // ── Generation ────────────────────────────────────────────────────────
  const pollJob = useCallback(
    async (jobId: string) => {
      const deadline = Date.now() + 180_000;
      while (Date.now() < deadline) {
        const res = await fetch(`/api/photoslop/jobs/${jobId}`, { cache: 'no-store' });
        const j = (await res.json()) as { status?: string; imagePath?: string; error?: string };
        if (j.status === 'done' && j.imagePath) {
          onPick(j.imagePath);
          return;
        }
        if (j.status === 'failed') {
          onError?.(j.error ?? 'Generation failed');
          return;
        }
        await new Promise((r) => setTimeout(r, 1500));
      }
      onError?.('Timed out waiting for image');
    },
    [onError, onPick],
  );

  const run = async () => {
    if (!prompt.trim()) {
      onError?.('Write a prompt first.');
      return;
    }
    if (filledCount === 0) {
      onError?.(
        'Pick at least one reference image (or remove the requirement by adding a category).',
      );
      return;
    }
    setBusy(true);
    setStatus('Starting…');
    try {
      const images = slots.filter((s): s is SlotEntry => s !== null).map((s) => s.path);
      const res = await fetch('/api/photoslop/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ images, prompt, aspectRatio: '16:9' }),
      });
      const data = (await res.json()) as { jobId?: string; error?: string };
      if (!data.jobId) {
        onError?.(data.error ?? `Request failed (${res.status})`);
        return;
      }
      setStatus('Rendering…');
      await pollJob(data.jobId);
    } catch (e) {
      onError?.(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setStatus('');
    }
  };

  return (
    <div className="flex flex-col gap-4 p-1">
      <header className="flex items-baseline justify-between">
        <p className="font-dank-mono text-[10px] uppercase tracking-widest text-slate-400">
          Nano Banana · 16:9
        </p>
        <span className="font-dank-mono text-[10px] text-slate-400">
          {filledCount}/{MAX_SLOTS} refs
        </span>
      </header>

      <SlotGrid
        slots={slots}
        onClearSlot={clearSlot}
        onPick={(entry, idx) => setSlot(idx, entry)}
        onAddRef={(entry) => addToFirstEmpty(entry)}
      />

      <RefChipBar slots={slots} onInsert={insertToken} dangling={danglingRefs} />

      <label className="font-inter text-xs text-slate-700">
        Prompt
        <textarea
          ref={promptRef}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={5}
          placeholder={
            'Describe the scene. Use @1, @2 to cite refs.\n' +
            'e.g. “@1 standing next to @2 at golden hour, soft bokeh, cinematic.”'
          }
          className="mt-1 w-full rounded-md border border-beige bg-white px-2 py-2 font-inter text-xs text-slate-800 placeholder:text-slate-400"
        />
      </label>

      {status && <p className="font-inter text-xs text-marine-green">{status}</p>}

      <button
        type="button"
        disabled={busy}
        onClick={run}
        className="rounded-full bg-marine-green px-4 py-2 font-inter text-xs font-semibold text-white hover:bg-marine-green/90 disabled:opacity-50"
      >
        {busy ? 'Working…' : 'Generate background'}
      </button>
    </div>
  );
}

// ── Slot grid ───────────────────────────────────────────────────────────
function SlotGrid({
  slots,
  onClearSlot,
  onPick,
  onAddRef,
}: {
  slots: (SlotEntry | null)[];
  onClearSlot: (idx: number) => void;
  onPick: (entry: SlotEntry, idx: number) => void;
  onAddRef: (entry: SlotEntry) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [activeSlot, setActiveSlot] = useState<number | null>(null);

  const openPickerFor = (idx: number) => {
    setActiveSlot(idx);
    setPickerOpen(true);
  };

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
        {slots.map((slot, idx) => (
          <SlotTile
            key={idx}
            index={idx}
            slot={slot}
            onClick={() => openPickerFor(idx)}
            onClear={() => onClearSlot(idx)}
          />
        ))}
      </div>

      {pickerOpen && (
        <RefPicker
          onClose={() => setPickerOpen(false)}
          onPick={(entry) => {
            if (activeSlot != null && slots[activeSlot] === null) {
              onPick(entry, activeSlot);
            } else {
              onAddRef(entry);
            }
          }}
        />
      )}
    </div>
  );
}

function SlotTile({
  index,
  slot,
  onClick,
  onClear,
}: {
  index: number;
  slot: SlotEntry | null;
  onClick: () => void;
  onClear: () => void;
}) {
  const label = `@${index + 1}`;
  if (!slot) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={`Add reference ${label}`}
        className="group relative flex aspect-square items-center justify-center rounded-lg border border-dashed border-beige bg-cream-alt text-slate-400 transition hover:border-marine-green hover:text-marine-green"
      >
        <span className="font-dank-mono text-[10px] uppercase tracking-widest">{label}</span>
        <span className="absolute bottom-1 right-1.5 font-dank-mono text-[9px] uppercase">+</span>
      </button>
    );
  }
  return (
    <div className="group relative aspect-square overflow-hidden rounded-lg border border-beige bg-white">
      <img src={slot.path} alt={slot.label} className="h-full w-full object-cover" />
      <span className="absolute left-1 top-1 rounded bg-black/55 px-1.5 py-0.5 font-dank-mono text-[9px] font-bold text-white">
        {label}
      </span>
      <button
        type="button"
        onClick={onClear}
        title="Remove reference"
        aria-label="Remove reference"
        className="absolute right-1 top-1 hidden rounded-full bg-black/70 px-1.5 py-0.5 font-dank-mono text-[9px] text-white hover:bg-red-500 group-hover:block"
      >
        ×
      </button>
    </div>
  );
}

// ── Cite chips (insert @N into prompt) ───────────────────────────────────
function RefChipBar({
  slots,
  onInsert,
  dangling,
}: {
  slots: (SlotEntry | null)[];
  onInsert: (slotIndex: number) => void;
  dangling: number[];
}) {
  const filled = slots
    .map((entry, i) => ({ entry, i }))
    .filter((x): x is { entry: SlotEntry; i: number } => x.entry !== null);

  if (filled.length === 0 && dangling.length === 0) {
    return (
      <p className="font-dank-mono text-[10px] leading-snug text-slate-400">
        Pick a reference above, then use <code>@1</code> / <code>@2</code> in the prompt to cite it.
      </p>
    );
  }

  return (
    <div className="space-y-1.5">
      {filled.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-dank-mono text-[10px] font-bold uppercase tracking-wide text-slate-400">
            Cite
          </span>
          {filled.map(({ entry, i }) => {
            const n = i + 1;
            return (
              <button
                key={i}
                type="button"
                onClick={() => onInsert(i)}
                title={`Insert @${n}`}
                className="group inline-flex items-center gap-1.5 rounded-full border border-beige bg-white py-0.5 pl-1 pr-2 font-inter text-[11px] font-semibold text-slate-700 shadow-sm transition hover:border-marine-green hover:bg-marine-green/5 hover:text-marine-green"
              >
                <img
                  src={entry.path}
                  alt={entry.label}
                  className="h-4 w-4 flex-shrink-0 rounded-full object-cover"
                />
                <span className="font-dank-mono text-[10px] font-bold text-marine-green">@{n}</span>
                <span className="max-w-[80px] truncate text-slate-500 group-hover:text-marine-green">
                  {entry.label}
                </span>
              </button>
            );
          })}
        </div>
      )}
      {dangling.length > 0 && (
        <p className="rounded-lg border border-coral/30 bg-coral/10 px-2.5 py-1.5 font-dank-mono text-[10px] leading-snug text-coral">
          {dangling.map((n) => `@${n}`).join(', ')} not filled — add a reference image or remove the
          token from the prompt.
        </p>
      )}
    </div>
  );
}

// ── Reference picker (library / brand / upload) ─────────────────────────
function RefPicker({
  onClose,
  onPick,
}: {
  onClose: () => void;
  onPick: (entry: SlotEntry) => void;
}) {
  const [source, setSource] = useState<PickerSource>('library');

  const select = (entry: SlotEntry) => {
    onPick(entry);
    // Keep picker open so the user can stack multiple refs in one session.
  };

  return (
    <div className="rounded-xl border border-beige bg-white p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex gap-1 rounded-lg border border-beige bg-cream-alt p-1">
          {(
            [
              { id: 'library' as const, label: 'Library' },
              { id: 'brand' as const, label: 'Brand' },
              { id: 'upload' as const, label: 'Upload' },
            ] as const
          ).map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setSource(t.id)}
              className={`rounded-md px-2.5 py-1 font-inter text-[11px] font-semibold transition ${
                source === t.id ? 'bg-marine-green text-white' : 'text-slate-600 hover:bg-white'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-full border border-beige px-2 py-0.5 font-dank-mono text-[10px] text-slate-500 hover:border-marine-green hover:text-marine-green"
        >
          Done
        </button>
      </div>

      <div className="max-h-[280px] overflow-y-auto">
        {source === 'library' && <LibraryPicker onPick={select} />}
        {source === 'brand' && <BrandPicker onPick={select} />}
        {source === 'upload' && <UploadPicker onPick={select} />}
      </div>
    </div>
  );
}

function LibraryPicker({ onPick }: { onPick: (entry: SlotEntry) => void }) {
  const [data, setData] = useState<LibraryResponse | null>(null);
  const [tab, setTab] = useState<LibraryTab>('photoslop');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch('/api/photoslop/library', { cache: 'no-store' });
        if (!res.ok) return;
        const json = (await res.json()) as LibraryResponse;
        if (!cancelled) setData(json);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const items = (data?.[tab] ?? []).filter((i) =>
    search.trim() ? i.filename.toLowerCase().includes(search.toLowerCase()) : true,
  );

  const tabs: { key: LibraryTab; label: string }[] = [
    { key: 'photoslop', label: 'Photoslop' },
    { key: 'uploads', label: 'Uploads' },
    { key: 'generated', label: 'Generated' },
    { key: 'exports', label: 'Exports' },
  ];

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="grid flex-1 grid-cols-4 gap-1 rounded-md border border-beige bg-cream-alt p-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`rounded px-1.5 py-1 font-inter text-[10px] font-semibold ${
                tab === t.key ? 'bg-marine-green text-white' : 'text-slate-600 hover:bg-white'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search…"
          className="w-28 rounded-md border border-beige bg-white px-2 py-1 font-inter text-[11px]"
        />
      </div>
      {loading ? (
        <p className="py-6 text-center font-inter text-xs text-slate-400">Loading…</p>
      ) : items.length === 0 ? (
        <p className="py-6 text-center font-inter text-xs text-slate-400">Nothing here yet.</p>
      ) : (
        <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-4">
          {items.map((item) => (
            <button
              key={item.path}
              type="button"
              onClick={() => onPick({ path: item.path, label: item.filename })}
              className="group aspect-square overflow-hidden rounded-md border border-beige bg-cream-alt transition hover:border-marine-green"
              title={item.filename}
            >
              <img src={item.path} alt={item.filename} className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function BrandPicker({ onPick }: { onPick: (entry: SlotEntry) => void }) {
  const [brands, setBrands] = useState<BrandsResponse | null>(null);
  const [activeBrand, setActiveBrand] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch('/api/photoslop/brands', { cache: 'no-store' });
        if (!res.ok) return;
        const json = (await res.json()) as BrandsResponse;
        if (!cancelled) {
          setBrands(json);
          setActiveBrand(json.folders[0]?.key ?? null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return <p className="py-6 text-center font-inter text-xs text-slate-400">Loading brands…</p>;
  }
  if (!brands || brands.folders.length === 0) {
    return (
      <p className="py-6 text-center font-inter text-xs text-slate-400">
        No brand subjects in ~/.openclaw/workspace/brand/
      </p>
    );
  }

  const assets = activeBrand ? (brands.assetsByFolder[activeBrand] ?? []) : [];

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1">
        {brands.folders.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setActiveBrand(f.key)}
            className={`rounded-full border px-2.5 py-0.5 font-inter text-[11px] font-semibold transition ${
              activeBrand === f.key
                ? 'border-marine-green bg-marine-green text-white'
                : 'border-beige text-slate-600 hover:border-marine-green'
            }`}
          >
            {f.label} <span className="font-dank-mono text-[9px] opacity-70">·{f.count}</span>
          </button>
        ))}
      </div>
      {assets.length === 0 ? (
        <p className="py-6 text-center font-inter text-xs text-slate-400">
          No assets in this brand yet.
        </p>
      ) : (
        <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-4">
          {assets.slice(0, 24).map((a) => (
            <button
              key={a.path}
              type="button"
              onClick={() =>
                onPick({
                  path: a.path,
                  label: `${activeBrand?.replace(/-ref$/, '') ?? 'brand'}/${a.filename}`,
                })
              }
              className="group aspect-square overflow-hidden rounded-md border border-beige bg-cream-alt transition hover:border-marine-green"
              title={`${a.variant ? a.variant + '/' : ''}${a.filename}`}
            >
              <img src={a.path} alt={a.filename} className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function UploadPicker({ onPick }: { onPick: (entry: SlotEntry) => void }) {
  const [busy, setBusy] = useState(false);

  const upload = async (file: File) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch('/api/photoslop/upload', { method: 'POST', body: fd });
      if (!res.ok) {
        alert(`Upload failed (${res.status})`);
        return;
      }
      const data = (await res.json()) as { path?: string };
      if (data.path) onPick({ path: data.path, label: file.name });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-3 py-5">
      <p className="text-center font-inter text-xs text-slate-500">
        Upload a fresh image to use as a reference.
      </p>
      <label className="cursor-pointer rounded-full bg-marine-green px-4 py-1.5 font-inter text-xs font-semibold text-white hover:bg-marine-green/90">
        {busy ? 'Uploading…' : 'Choose image'}
        <input
          type="file"
          accept="image/*"
          className="hidden"
          disabled={busy}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) upload(f);
            e.target.value = '';
          }}
        />
      </label>
    </div>
  );
}
